
import { computed, defineComponent, ref } from 'vue'
import { useProfile } from '@/composable/useProfile'
import { routerPush } from '@/router'
import AppLogo from '@/components/AppLogo.vue'
import AppAccountDropdown from '@/components/AppAccountDropdown.vue'
import { onBeforeRouteLeave } from 'vue-router'
import { getDefaultRoute } from '@/router/priorityDefaultRouteFromProject'
import orderBy from 'lodash.orderby'

// Projekt som alltid ska till V2, oavsett behörigheter
const V2_ONLY_PROJECTS = ['pendeltag']

export default defineComponent({
  name: 'Projects',

  components: {
    AppLogo,
    AppAccountDropdown,
  },

  setup() {
    const handleGoToAvikV2 = (isDev: boolean) => {
      localStorage.setItem('isAvikDev', isDev ? 'true' : 'false')
      window.location.href = 'https://avik.sj.se/v2'
    }
    const { projects, profile, devProjects, devProfile } = useProfile()

    const hidePage = ref(true)

    onBeforeRouteLeave((to) => {
      window.location.pathname = to.path
    })

    const totalProjectsCount = computed(
      () => projects.value.length + (devProjects.value?.length || 0)
    )

    // DEBUG LOGGING
    console.group('Avik Routing Debug Info')
    console.log(
      'Prod projects:',
      projects.value.map((p) => p.name)
    )
    console.log('Dev projects:', devProjects.value?.map((p) => p.name) || [])

    const projectsWithV2 = projects.value
      .filter((p) => projectHasV2(p.name, false))
      .map((p) => p.name)
    const devProjectsWithV2 =
      devProjects.value
        ?.filter((p) => projectHasV2(p.name, true))
        .map((p) => p.name) || []

    console.log('Prod projects with V2 access:', projectsWithV2)
    console.log('Dev projects with V2 access:', devProjectsWithV2)

    let decision = 'Show projects page (hidePage = false)'

    if (totalProjectsCount.value === 1) {
      const isDev = projects.value.length === 0
      const theProject = isDev ? devProjects.value[0] : projects.value[0]
      const projectName = theProject.name
      const profileToUse = isDev ? devProfile.value : profile.value
      const perms = profileToUse?.permissions[projectName] || []

      const hasV2Perm =
        V2_ONLY_PROJECTS.includes(projectName) ||
        perms.some((p) => p.startsWith('v2'))

      if (hasV2Perm) {
        decision = `Auto-redirect to V2 (${
          isDev ? 'Dev' : 'Prod'
        }) for project ${projectName}`
        console.log('Decision would be:', decision)
        console.groupEnd()
        handleGoToAvikV2(isDev)
      } else {
        decision = `Auto-redirect to V1 (${
          isDev ? 'Dev' : 'Prod'
        }) for project ${projectName}`
        console.log('Decision would be:', decision)
        console.groupEnd()
        openProject(projectName, isDev)
      }
    } else {
      console.log('Decision would be:', decision)
      console.groupEnd()
      hidePage.value = false
    }

    function projectHasV2(projectName: string, isDev: boolean) {
      const activeProfile = isDev ? devProfile.value : profile.value
      if (!activeProfile) return false
      const perms = activeProfile.permissions[projectName] || []
      return perms.some((p) => p.startsWith('v2'))
    }

    function projectHasOnlyV2(projectName: string, isDev: boolean) {
      if (V2_ONLY_PROJECTS.includes(projectName)) return true
      const activeProfile = isDev ? devProfile.value : profile.value
      if (!activeProfile) return false
      const perms = activeProfile.permissions[projectName] || []
      return perms.length > 0 && perms.every((p) => p.startsWith('v2'))
    }

    function openProject(project: string, isDev: boolean) {
      if (isDev) localStorage.setItem('isAvikDev', 'true')
      if (!isDev) localStorage.setItem('isAvikDev', 'false')
      if (hasMasterProject(project)) {
        routerPush('masterdata', { project })
      } else if (isDev) {
        if (!devProfile.value) {
          throw new Error('Dev Profile permissions not found')
        }
        routerPush(getDefaultRoute(devProfile.value.permissions[project]), {
          project,
        })
      } else {
        if (!profile.value) {
          throw new Error('Profile permissions not found')
        }
        routerPush(getDefaultRoute(profile.value.permissions[project]), {
          project,
        })
      }
    }

    function hasMasterProject(projectName: string) {
      return projectName === 'master'
    }

    return {
      openProject,
      projects,
      hasMasterProject,
      hidePage,
      orderBy,
      handleGoToAvikV2,
      projectHasV2,
      projectHasOnlyV2,
      devProjects,
    }
  },
})
